import React, { useEffect, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Plus, Filter, LayoutList, Wrench } from 'lucide-react';
import { ToolCard } from '@/components/automation/tools/ToolCard';
import { CustomToolsTab } from '@/components/automation/tools/CustomToolsTab';
import { fetchPrebuildSelectedApps, selectPrebuiltActions } from '@/store/slices/prebuiltActionsSlice';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { PrebuiltAction } from '@/services/prebuiltActionsService';
import { usePageHeader } from '@/contexts/PageHeaderContext';

// Comprehensive tools data
const integratedTools = [{
  name: 'Google Calendar',
  description: 'Create events, manage schedules, and sync calendar data automatically',
  category: 'Productivity',
  connected: true,
  lastUsed: '1 hour ago',
  totalActions: 2156,
  activeActions: 23,
  connectedAccount: 'john@company.com',
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Notion',
  description: 'Create pages, update databases, and manage workspace content',
  category: 'Productivity',
  connected: true,
  lastUsed: '2 hours ago',
  totalActions: 1843,
  activeActions: 15,
  connectedAccount: 'workspace@company.com',
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Google Sheets',
  description: 'Create, update, and manage spreadsheets with automatic data sync',
  category: 'Productivity',
  connected: true,
  lastUsed: '30 minutes ago',
  totalActions: 3421,
  activeActions: 31,
  connectedAccount: 'data@company.com',
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Slack',
  description: 'Post messages, create channels, and manage team communications',
  category: 'Communication',
  connected: true,
  lastUsed: '15 minutes ago',
  totalActions: 4567,
  activeActions: 42,
  connectedAccount: 'Company Workspace',
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Google Docs',
  description: 'Create documents, collaborate on content, and manage document workflows',
  category: 'Productivity',
  connected: true,
  lastUsed: '3 hours ago',
  totalActions: 1234,
  activeActions: 18,
  connectedAccount: 'docs@company.com',
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Google Drive',
  description: 'Upload files, create folders, and share documents automatically',
  category: 'Storage',
  connected: true,
  lastUsed: '1 hour ago',
  totalActions: 2876,
  activeActions: 25,
  connectedAccount: 'drive@company.com',
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'HubSpot',
  description: 'Sync contacts, track deals, and automate marketing campaigns',
  category: 'CRM',
  connected: true,
  lastUsed: '45 minutes ago',
  totalActions: 3245,
  activeActions: 29,
  connectedAccount: 'marketing@company.com',
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Linear',
  description: 'Create issues, update project status, and manage development workflows',
  category: 'Project Management',
  connected: false,
  lastUsed: 'Never',
  totalActions: 0,
  activeActions: 0,
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Airtable',
  description: 'Manage databases, create records, and sync data across tables',
  category: 'Productivity',
  connected: true,
  lastUsed: '2 hours ago',
  totalActions: 1567,
  activeActions: 12,
  connectedAccount: 'data@company.com',
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Jira',
  description: 'Create tickets, track progress, and manage software development projects',
  category: 'Project Management',
  connected: true,
  lastUsed: '4 hours ago',
  totalActions: 2134,
  activeActions: 19,
  connectedAccount: 'dev@company.com',
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Discord',
  description: 'Send messages, manage servers, and automate community interactions',
  category: 'Communication',
  connected: false,
  lastUsed: 'Never',
  totalActions: 0,
  activeActions: 0,
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Microsoft Teams',
  description: 'Post messages, schedule meetings, and manage team collaboration',
  category: 'Communication',
  connected: true,
  lastUsed: '6 hours ago',
  totalActions: 1876,
  activeActions: 14,
  connectedAccount: 'teams@company.com',
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Asana',
  description: 'Create tasks, update projects, and manage team productivity',
  category: 'Project Management',
  connected: true,
  lastUsed: '3 hours ago',
  totalActions: 2345,
  activeActions: 21,
  connectedAccount: 'projects@company.com',
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Shopify',
  description: 'Manage products, orders, and customer data in your store',
  category: 'E-commerce',
  connected: true,
  lastUsed: '2 hours ago',
  totalActions: 3456,
  activeActions: 27,
  connectedAccount: 'store@company.com',
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'OneDrive',
  description: 'Store files, sync documents, and manage cloud storage',
  category: 'Storage',
  connected: false,
  lastUsed: 'Never',
  totalActions: 0,
  activeActions: 0,
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'DocuSign',
  description: 'Send documents for signature, track signing status, and manage contracts',
  category: 'Productivity',
  connected: true,
  lastUsed: '1 day ago',
  totalActions: 567,
  activeActions: 8,
  connectedAccount: 'contracts@company.com',
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Salesforce',
  description: 'Manage leads, contacts, and opportunities in your CRM',
  category: 'CRM',
  connected: true,
  lastUsed: '30 minutes ago',
  totalActions: 4321,
  activeActions: 35,
  connectedAccount: 'sales@company.com',
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Calendly',
  description: 'Schedule meetings, manage availability, and automate booking workflows',
  category: 'Productivity',
  connected: true,
  lastUsed: '4 hours ago',
  totalActions: 1234,
  activeActions: 16,
  connectedAccount: 'booking@company.com',
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Trello',
  description: 'Create cards, move tasks, and manage project boards',
  category: 'Project Management',
  connected: false,
  lastUsed: 'Never',
  totalActions: 0,
  activeActions: 0,
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'ClickUp',
  description: 'Manage tasks, track time, and organize project workflows',
  category: 'Project Management',
  connected: true,
  lastUsed: '5 hours ago',
  totalActions: 1789,
  activeActions: 13,
  connectedAccount: 'productivity@company.com',
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Attio',
  description: 'Manage contacts, track relationships, and sync CRM data',
  category: 'CRM',
  connected: false,
  lastUsed: 'Never',
  totalActions: 0,
  activeActions: 0,
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Google Meet',
  description: 'Schedule meetings, create video calls, and manage virtual events',
  category: 'Communication',
  connected: true,
  lastUsed: '2 hours ago',
  totalActions: 876,
  activeActions: 9,
  connectedAccount: 'meetings@company.com',
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Zoho',
  description: 'Manage business processes, sync data, and automate workflows',
  category: 'CRM',
  connected: true,
  lastUsed: '1 day ago',
  totalActions: 1456,
  activeActions: 11,
  connectedAccount: 'business@company.com',
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Dropbox',
  description: 'Store files, sync folders, and share documents securely',
  category: 'Storage',
  connected: true,
  lastUsed: '6 hours ago',
  totalActions: 2134,
  activeActions: 17,
  connectedAccount: 'storage@company.com',
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Confluence',
  description: 'Create documentation, manage knowledge base, and collaborate on content',
  category: 'Productivity',
  connected: false,
  lastUsed: 'Never',
  totalActions: 0,
  activeActions: 0,
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Monday',
  description: 'Manage projects, track progress, and automate team workflows',
  category: 'Project Management',
  connected: true,
  lastUsed: '3 hours ago',
  totalActions: 1987,
  activeActions: 15,
  connectedAccount: 'workflow@company.com',
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Zendesk',
  description: 'Manage tickets, track customer support, and automate help desk workflows',
  category: 'Customer Support',
  connected: true,
  lastUsed: '1 hour ago',
  totalActions: 3567,
  activeActions: 28,
  connectedAccount: 'support@company.com',
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Zoom',
  description: 'Schedule meetings, create webinars, and manage video conferences',
  category: 'Communication',
  connected: true,
  lastUsed: '4 hours ago',
  totalActions: 1345,
  activeActions: 12,
  connectedAccount: 'video@company.com',
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'SurveyMonkey',
  description: 'Create surveys, collect responses, and analyze feedback data',
  category: 'Analytics',
  connected: false,
  lastUsed: 'Never',
  totalActions: 0,
  activeActions: 0,
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Zoho Books',
  description: 'Manage invoices, track expenses, and automate accounting workflows',
  category: 'Finance',
  connected: true,
  lastUsed: '1 day ago',
  totalActions: 987,
  activeActions: 7,
  connectedAccount: 'finance@company.com',
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Zoho Inventory',
  description: 'Track stock levels, manage orders, and sync inventory data',
  category: 'E-commerce',
  connected: false,
  lastUsed: 'Never',
  totalActions: 0,
  activeActions: 0,
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Front',
  description: 'Manage shared inboxes, track conversations, and automate email workflows',
  category: 'Communication',
  connected: true,
  lastUsed: '2 hours ago',
  totalActions: 2456,
  activeActions: 19,
  connectedAccount: 'inbox@company.com',
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Atlassian',
  description: 'Integrate with Jira, Confluence, and other Atlassian products',
  category: 'Project Management',
  connected: true,
  lastUsed: '5 hours ago',
  totalActions: 1678,
  activeActions: 14,
  connectedAccount: 'atlassian@company.com',
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Blackbaud',
  description: 'Manage donor data, track fundraising, and automate nonprofit workflows',
  category: 'CRM',
  connected: false,
  lastUsed: 'Never',
  totalActions: 0,
  activeActions: 0,
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Gorgias',
  description: 'Manage customer support tickets and automate helpdesk responses',
  category: 'Customer Support',
  connected: true,
  lastUsed: '3 hours ago',
  totalActions: 1234,
  activeActions: 10,
  connectedAccount: 'helpdesk@company.com',
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Square',
  description: 'Process payments, manage transactions, and sync payment data',
  category: 'Finance',
  connected: true,
  lastUsed: '6 hours ago',
  totalActions: 2789,
  activeActions: 22,
  connectedAccount: 'payments@company.com',
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Kommo',
  description: 'Manage leads, track sales pipeline, and automate CRM workflows',
  category: 'CRM',
  connected: false,
  lastUsed: 'Never',
  totalActions: 0,
  activeActions: 0,
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Gumroad',
  description: 'Manage digital products, track sales, and automate creator workflows',
  category: 'E-commerce',
  connected: true,
  lastUsed: '1 day ago',
  totalActions: 567,
  activeActions: 5,
  connectedAccount: 'creator@company.com',
  logoUrl: '/api/placeholder/40/40'
}, {
  name: 'Intercom',
  description: 'Manage customer conversations, send messages, and automate support workflows',
  category: 'Customer Support',
  connected: true,
  lastUsed: '2 hours ago',
  totalActions: 3456,
  activeActions: 26,
  connectedAccount: 'chat@company.com',
  logoUrl: '/api/placeholder/40/40'
}];
const ToolsPage = () => {
  const dispatch = useAppDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const [integratedTools, setIntegratedTools] = useState<PrebuiltAction[]>([]);
  // const prebuiltActions = useSelector((state: RootState) => state.prebuiltActions.items);
  // console.log('prebuiltActions', prebuiltActions);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const {
    setHeaderData
  } = usePageHeader();
  const categories = ['all', 'Communication', 'Productivity', 'CRM', 'Project Management', 'E-commerce', 'Storage', 'Customer Support', 'Analytics', 'Finance'];
  const filteredTools = integratedTools?.filter(tool => {
    const matchesSearch = tool.name.toLowerCase().includes(searchTerm.toLowerCase()) || tool.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || tool.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Set header data for dynamic header
  useEffect(() => {
    const connectedCount = integratedTools.filter(tool => tool.connected).length;
    const totalActions = integratedTools.reduce((acc, tool) => acc + (tool.totalActions || 0), 0);
    setHeaderData({
      title: 'Tools',
      description: 'Connect and manage external tools for your automations',
      icon: Wrench,
      stats: [{
        label: 'Available Tools',
        value: integratedTools.length,
        color: 'text-gray-900',
        dotColor: 'bg-blue-500'
      }, {
        label: 'Connected',
        value: connectedCount,
        color: 'text-emerald-600',
        dotColor: 'bg-emerald-500'
      }, {
        label: 'Total Actions',
        value: totalActions,
        color: 'text-purple-600',
        dotColor: 'bg-purple-500'
      }]
    });
  }, [integratedTools, setHeaderData]);
  useEffect(() => {
    dispatch(fetchPrebuildSelectedApps()).unwrap().then(data => {
      setIntegratedTools(data);
    });
  }, []);
  const handleConnect = (toolName: string) => {
    console.log('Connecting to:', toolName);
  };
  const handleDisconnect = (toolName: string) => {
    console.log('Disconnecting from:', toolName);
  };
  return <div className="min-h-screen bg-gray-50/30">
      <div className="max-w-7xl mx-auto p-6 space-y-8">
        {/* Header Section */}
        

        <Tabs defaultValue="integrated" className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-1">
            <TabsList className="grid w-full grid-cols-2 bg-gray-50">
              <TabsTrigger value="integrated" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
                Integrated Tools
              </TabsTrigger>
              <TabsTrigger value="custom" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
                Custom Tools
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="integrated" className="space-y-6">
            {/* Search and Filter Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input placeholder="Search tools..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 h-11 border-gray-200 focus:border-blue-500 focus:ring-blue-500/20" />
                </div>
                <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white min-w-[200px]">
                  {categories.map(category => <option key={category} value={category}>
                      {category === 'all' ? 'All Categories' : category}
                    </option>)}
                </select>
              </div>
            </div>

            {/* Tools List Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Available Tools ({filteredTools.length})
                  </h2>
                  <div className="text-sm text-gray-500">
                    {filteredTools.filter(tool => tool.connected).length} connected
                  </div>
                </div>
                
                <div className="space-y-1">
                  {filteredTools.map((tool, index) => <ToolCard key={index} tool={tool} onConnect={() => handleConnect(tool.name)} onDisconnect={() => handleDisconnect(tool.name)} />)}
                </div>
              </div>
            </div>

            {filteredTools.length === 0 && <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Filter className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No tools found</h3>
                  <p className="text-gray-600 max-w-sm mx-auto">
                    Try adjusting your search terms or filter criteria to find the tools you're looking for.
                  </p>
                </div>
              </div>}
          </TabsContent>

          <TabsContent value="custom">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 min-h-[400px]">
              <CustomToolsTab />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>;
};
export default ToolsPage;